import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'

import {
  FileDrop,
  Success,
  UploadProvider,
  useUpload,
  UIStage,
} from '@/components/upload'
import { Modal } from '@/components/Modal'
import { ReferenceSearch } from '@/components/ReferenceSearchForm'
import { SpeciesForm } from '@/components/upload/SpeciesForm'

const ErrorComponent = () => {
  const {
    state: { validationErrors, successRows },
    handleReset,
  } = useUpload()

  return (
    validationErrors && (
      <div className="w-full rounded-md border border-red-300 p-4 text-sm flex flex-col">
        <span className="font-bold">
          File was {successRows ? 'partially uploaded' : 'not uploaded'} due to
          errors
        </span>
        {validationErrors &&
          Object.values(validationErrors).map((error, i) => (
            <span key={i}>{error}</span>
          ))}
        <button
          onClick={handleReset}
          className="mt-4 bg-blue-500 text-white rounded-md p-2 w-24"
        >
          Reset form
        </button>
      </div>
    )
  )
}

const UploadInner = () => {
  const { state, setDryRun } = useUpload()
  const [infoOpen, setInfoOpen] = useState(false)
  const [referenceSearchOpen, setReferenceSearchOpen] = useState(false)
  const [taxonFormOpen, setTaxonFormOpen] = useState(false)

  return (
    <>
      <div className="mt-6 flex flex-col gap-2">
        <div className="flex justify-between">
          <h4 className="text-xl font-bold text-gray-800">Upload</h4>
          <span className="flex gap-2 items-center">
            <label>
              <input
                title="Select this option to test the upload without actually uploading data"
                type="checkbox"
                onChange={(e) => setDryRun(e.target.checked)}
              />{' '}
              Dry run
            </label>
            <button onClick={() => setReferenceSearchOpen(true)}>
              🔎 Reference search
            </button>
            <button onClick={() => setTaxonFormOpen(true)}>🌿 New Taxon</button>
            <button onClick={() => setInfoOpen(true)}>
              ℹ️ How to use this page
            </button>
          </span>
        </div>
        <div className="flex gap-4">
          <div className="flex flex-col gap-8 sm:w-full w-3/4">
            {(!state ||
              state.stage === UIStage.READY ||
              state.stage === UIStage.FILE_ERROR ||
              state.stage === UIStage.PROCESSING) && <FileDrop />}
            {state.stage === UIStage.SUCCESS && <Success />}
            {state.stage === UIStage.ERROR && <ErrorComponent />}
          </div>
          <div className="sm:w-full md:w-1/4 border border-gray-500 rounded-lg p-4">
            <h4 className="font-bold text-gray-800">Template files</h4>

            <ul className="mt-4 list-none">
              <li className="text-serblue-dark">
                <a href="/upload_templates/germination_sid_upload.csv" download>
                  Germination
                </a>
              </li>
              <li className="text-serblue-dark">
                <a href="/upload_templates/oil_content_sid_upload.csv" download>
                  Oil Content
                </a>
              </li>
              <li className="text-serblue-dark">
                <a
                  href="/upload_templates/protein_content_sid_upload.csv"
                  download
                >
                  Protein Content
                </a>
              </li>
              <li className="text-serblue-dark">
                <a
                  href="/upload_templates/seed_weights_sid_upload.csv"
                  download
                >
                  Seed Weights
                </a>
              </li>
              <li className="text-serblue-dark">
                <a
                  href="/upload_templates/storage_behaviour_sid_upload.csv"
                  download
                >
                  Storage Behaviour
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <Modal
        title="New Taxon"
        open={taxonFormOpen}
        setOpen={setTaxonFormOpen}
        withClose
      >
        <SpeciesForm />
      </Modal>
      <Modal
        title="Search for a Reference ID"
        open={referenceSearchOpen}
        setOpen={setReferenceSearchOpen}
        withClose
      >
        <ReferenceSearch />
      </Modal>
      <Modal
        title="How to upload data to SID"
        open={infoOpen}
        setOpen={setInfoOpen}
      >
        <div className="flex flex-col gap-2">
          <ol className="list-decimal list-inside gap-2 flex flex-col text-sm">
            <li>
              Choose a template for the data type to upload. Each template file
              contains a header row with column names.
            </li>
            <li>
              Each template file contains a column called{' '}
              <span className="font-mono text-sm text-gray-600 px-1">
                taxon_name
              </span>
              . This is the name of the species you are uploading data for. The
              name must match an existing species in the SID database based on
              the initial characters. For example,{' '}
              <span className="font-mono text-sm text-gray-600 px-1">
                Acac acumi
              </span>{' '}
              would match{' '}
              <span className="font-mono text-sm text-gray-600 px-1">
                Acacia acuminata
              </span>{' '}
              in the SID database. Taxon names must match exactly one existing
              taxon in the database.
            </li>
            <li>
              The templates include a column called{' '}
              <span className="font-mono text-sm text-gray-600 px-1">
                reference_id
              </span>
              . This is a unique identifier for the publication reference that
              the data derives from. If you don't know the{' '}
              <span className="font-mono text-sm text-gray-600 px-1">
                reference_id
              </span>{' '}
              in the SID database, you can leave it blank and use the{' '}
              <span className="font-mono text-sm text-gray-600 px-1">
                new_reference_citation
              </span>{' '}
              and{' '}
              <span className="font-mono text-sm text-gray-600 px-1">
                new_reference_description
              </span>{' '}
              fields. These fields allow you to add a new reference to the SID
              database, or find an existing one with exactly matching details.
              The citation should be in the format of{' '}
              <span className="italic">Author, Date</span>. The description
              should be the full bibliographical reference.
            </li>
            <li>
              Some of the templates contain a column called{' '}
              <span className="font-mono text-sm text-gray-600 px-1">
                material
              </span>
              . The{' '}
              <span className="font-mono text-sm text-gray-600 px-1">
                material
              </span>{' '}
              column accepts the following values:{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Nut
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Achene
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Bract+enclosed achene
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Burr
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Capsule
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Caryopsis
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Caryopsis+palea
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Caryopsis+palea+lemma
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Cypsela
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                De-pulped drupe
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed excluding wings
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Diaspore
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed excluding seed-coat
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed excluding endocarp
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                "Five united, one-seeded carpels"
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Fruit
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Fruiting calyx
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed+endocarp
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Involucre
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Nutlet
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Samara
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed cleaned of pulp and wings (where appropriate)
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed excluding pericarp
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed+part of pericarp
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed+pericarp
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed+pericarp excluding wings
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed+pericarp+calyx
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed+pericarp+dewinged involucre
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed+pericarp+perianth
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Pod
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed+ovary tissues (e.g. achene)
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed+calyx
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed excluding pappus
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Germinule
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Pyrene
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Embryo+endosperm
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed cleaned of pulp
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed excluding "dispersal structures"
              </span>
              ,{' '}
              <span className="font-mono text-sm text-gray-600 px-[1px]">
                Seed including "dispersal structures"
              </span>{' '}
              .
            </li>
            <li>
              After completing the template file, drag and drop the file onto
              the page. The file will be validated and you will see a success
              message, or else a list of errors. If there were errors in
              validating any of the data, none of the data will be uploaded.
            </li>
            <li>
              In order to test the upload feature without actually uploading
              data, you can check the "Dry run" box. This will allow you to see
              the results of the upload and correct any errors before uploading
              the data.
            </li>
          </ol>
          <div className="flex justify-end">
            <button
              onClick={() => setInfoOpen(false)}
              className="mt-4 bg-blue-500 text-white rounded-md p-2 w-24"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

const UploadPage = () => {
  return (
    <UploadProvider>
      <UploadInner />
    </UploadProvider>
  )
}

export const Route = createFileRoute('/private/upload/')({
  component: UploadPage,
})
